import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ProductAttributeDefinitionsService } from '@src/app/shared/backend/clients';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { DialogReasonComponent } from '@src/app/shared/components/dialog-reason/dialog-reason.component';
import { ConfirmationService } from 'primeng/api';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-view-product-attribute-definitions',
    templateUrl: './view-product-attribute-definitions.component.html',
    styleUrl: './view-product-attribute-definitions.component.scss',
    host: {
        class: 'app-view-product-attribute-definitions'
    },
    providers: [ConfirmationService],
    standalone: false
})
export class ViewProductAttributeDefinitionsComponent extends BaseViewComponent implements OnInit, OnDestroy {
  private readonly destroyedComponent$ = new Subject<void>();

  @ViewChild('dialog') dialog: DialogReasonComponent | undefined;

  allowModifyProductAttribute = false;
  productAttributeDefinitionId = 0;
  stateOptions: any[];
  stateInitialValue: any;
  stateNewValue: any;
  sanitizedConfirmMessage: SafeHtml | undefined;
  productAttributesDefinition: any = null;

  constructor(
    private permissionService: PermissionService,
    private translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private confirmationService: ConfirmationService,
    private productAttributeDefinitionsService: ProductAttributeDefinitionsService,
  ) {
    super();
    this.updateStateOptions();
  }

  ngOnInit() {
    this.translate.onLangChange
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(() => {
        this.updateStateOptions();
      });

    this.init();
  }

  private async init(): Promise<void> {
    this.route.params
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(params => {
        this.productAttributeDefinitionId = Number(params['productAttributeDefinitionId']);
        if (this.productAttributeDefinitionId > 0) {
          this.get();
        }
      });

    this.allowModifyProductAttribute = await this.permissionService.hasActionPermissions(
      Actions.modify_productattributedefinitions
    );
  }

  private updateStateOptions(): void {
    this.stateOptions = [
      {
        label: this.translate.instant('BOOLEANS.ACTIVATE_SWITCH'),
        displayLabel: this.translate.instant('BOOLEANS.ACTIVATE_SWITCH'),
        value: true
      },
      {
        label: this.translate.instant('BOOLEANS.DEACTIVATE_SWITCH'),
        displayLabel: this.translate.instant('BOOLEANS.DEACTIVATE_SWITCH'),
        value: false
      }
    ];
  }

  initStateSwitch() {
    this.stateInitialValue = this.productAttributesDefinition.isActive;

    const selectedLabel = this.stateOptions.find(option => option.value === this.stateInitialValue)?.label.toLowerCase();
    const translatedMessage = this.translate.instant('MODALBOXES.CONFIRM_STATE_CHANGE', { state: selectedLabel });
    this.sanitizedConfirmMessage = this.sanitizer.bypassSecurityTrustHtml(translatedMessage);
  }

  onStateChange(event: any) {
    this.stateNewValue = event.value;
    const selectedLabel = this.stateOptions.find(option => option.value === this.stateNewValue)?.label.toLowerCase();

    const translatedMessage = this.translate.instant('MODALBOXES.CONFIRM_STATE_CHANGE', { state: selectedLabel });
    this.sanitizedConfirmMessage = this.sanitizer.bypassSecurityTrustHtml(translatedMessage);

    // State update confirmation dialog
    this.confirmationService.confirm({
      key: 'confirm-state-change-dialog',
      header: this.translate.instant('MODALBOXES.TITLE_CONFIRM_STATE_CHANGE')
    });
  }

  override async get() {
    try {
      this.productAttributesDefinition = await firstValueFrom(
        this.productAttributeDefinitionsService.apiProductAttributeDefinitionsDetailsIdGet(
          this.productAttributeDefinitionId
        )
      );
      this.initStateSwitch();
    } catch (error) {
      console.log('Error fetching product attribute definition data:', error);
    }
  }

  goToEditProductAttributesDefinitions() {
    this.router.navigate([
      `/product-attribute-definitions/${this.productAttributeDefinitionId}/edit`
    ]);
  }

  onConfirmStateChange(): void {
    if (this.stateNewValue === false) {
      this.productAttributeDefinitionsService.apiProductAttributeDefinitionsIdDeactivatePatch(this.productAttributeDefinitionId).subscribe({
        next: async () => {
          await this.get();
        },
        error: (error) => {
          console.error('Failed to deactivate retailer', error);
        }
      });
    }
    if (this.stateNewValue === true) {
      this.productAttributeDefinitionsService.apiProductAttributeDefinitionsIdActivatePatch(this.productAttributeDefinitionId).subscribe({
        next: async () => {
          await this.get();
        },
        error: (error) => {
          console.error('Failed to activate retailer', error);
        }
      });
    }
    this.confirmationService.close();
  }

  onCancelStateChange() {
    // Revert state change
    const nonMatchingOption = this.stateOptions.find(option => option.value !== this.stateInitialValue).value;
    this.stateInitialValue = nonMatchingOption;
    this.confirmationService.close();
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }
}
